import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './AddPatient.css';
import './IntakeForm.css';
import Layout from '../components/Layout';
import { intakeFormService } from '../services/intakeFormService';
import { domainService } from '../services/domainService';
import { authService } from '../services/authService';

const IntakeForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  // Determine mode and IDs from URL
  const isViewMode = location.pathname.includes('/view/');
  const isEditMode = location.pathname.includes('/edit/');
  const formId = isViewMode || isEditMode ? params.id : null;
  const routePatientId = !formId ? params.patientId : null;

  const [currentStep, setCurrentStep] = useState(formId ? 1 : (routePatientId ? 1 : 0)); // 0: Patient Selection, 1: Form, 2: Prompt
  const [isLoadingForm, setIsLoadingForm] = useState(!!formId);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [domains, setDomains] = useState([]);
  const [isLoadingDomains, setIsLoadingDomains] = useState(true);
  const [customFields, setCustomFields] = useState([]);
  const [showFieldModal, setShowFieldModal] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [prompt, setPrompt] = useState('');
  const fileInputRef = useRef(null);

  // Patient selection state
  const [patients, setPatients] = useState([]);
  const [selectedPatientId, setSelectedPatientId] = useState(routePatientId || '');
  const [isLoadingPatients, setIsLoadingPatients] = useState(true);
  const [showPatientSelection, setShowPatientSelection] = useState(!routePatientId);
  const [patientsData, setPatientsData] = useState([]);
  const [remainingRequests, setRemainingRequests] = useState(null);
  const [showLimitWarning, setShowLimitWarning] = useState(false);
  const [user, setUser] = useState(null);

  const [newField, setNewField] = useState({
    type: 'text',
    title: '',
    id: '',
    placeholder: ''
  });
  const [formData, setFormData] = useState({
    // Identifying Information
    fullName: '',
    dob: '',
    age: '',
    phoneNumber: '',
    emergencyContact: '',
    email: '',
    primaryLanguage: '',
    raceEthnicity: '',
    gender: '',
    bloodGroup: '',
    address: '',

    // Referral & Presenting Concerns
    chiefComplaint: '',
    hasRiskFactors: 'no',
    riskFactorsDetails: '',
    additionalComments: '',

    // Medical & Psychiatric History
    currentMedications: ''
  });

  // Get current user
  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    setUser(currentUser);
  }, []);

  // Fetch domains from API on component mount
  useEffect(() => {
    const fetchDomains = async () => {
      try {
        setIsLoadingDomains(true);
        const data = await domainService.getDomains();
        setDomains(data);
      } catch (error) {
        console.error('Error fetching domains:', error);
        setSaveError('Failed to load domains. Please refresh the page.');
      } finally {
        setIsLoadingDomains(false);
      }
    };

    fetchDomains();
  }, []);

  // Fetch remaining requests
  useEffect(() => {
    const fetchRemainingRequests = async () => {
      try {
        // Only check for new forms, not when editing
        if (!formId) {
          const data = await intakeFormService.getRemainingRequests();
          setRemainingRequests(data);
        }
      } catch (error) {
        console.error('Error fetching remaining requests:', error);
      }
    };

    fetchRemainingRequests();
  }, [formId]);

  // Fetch patients for selection
  useEffect(() => {
    const fetchPatients = async () => {
      try {
        setIsLoadingPatients(true);
        const data = await intakeFormService.getPatients();
        setPatientsData(data || []);
        setPatients(data || []);

        // If routePatientId provided, auto-select and skip selection screen
        if (routePatientId) {
          const selectedPatient = data?.find(p => p._id === routePatientId);
          if (selectedPatient) {
            setSelectedPatientId(routePatientId);
            setFormData(prev => ({
              ...prev,
              fullName: selectedPatient.fullName || '',
              email: selectedPatient.email || '',
              phoneNumber: selectedPatient.phoneNumber || '',
              age: selectedPatient.age || ''
            }));
            setCurrentStep(1); // Go directly to form step
            setShowPatientSelection(false);
          }
        }
      } catch (error) {
        console.error('Error fetching patients:', error);
      } finally {
        setIsLoadingPatients(false);
      }
    };

    fetchPatients();
  }, [routePatientId]);

  // Load existing intake form if formId is provided
  useEffect(() => {
    const loadIntakeForm = async () => {
      if (!formId) return;

      try {
        setIsLoadingForm(true);
        const data = await intakeFormService.getIntakeForm(formId);

        // Populate form with loaded data
        setFormData({
          // Identifying Information
          fullName: data.fullName || '',
          dob: data.dob || '',
          age: data.age || '',
          phoneNumber: data.phoneNumber || '',
          emergencyContact: data.emergencyContact || '',
          email: data.email || '',
          primaryLanguage: data.primaryLanguage || '',
          raceEthnicity: data.raceEthnicity || '',
          gender: data.gender || '',
          bloodGroup: data.bloodGroup || '',
          address: data.address || '',

          // Referral & Presenting Concerns
          chiefComplaint: data.chiefComplaint || '',
          hasRiskFactors: data.hasRiskFactors || 'no',
          riskFactorsDetails: data.riskFactorsDetails || '',
          additionalComments: data.additionalComments || '',

          // Medical & Psychiatric History
          currentMedications: data.currentMedications || ''
        });

        // Set selected patient ID
        if (data.patientId) {
          setSelectedPatientId(data.patientId);
        }

        // Set domain if available
        if (data.selectedDomain) {
          setSelectedDomain(data.selectedDomain);
        }

        // Set prompt if available
        if (data.prompt) {
          setPrompt(data.prompt);
        }

        // Set custom fields if available
        if (data.customFields) {
          const customFieldsArray = Object.keys(data.customFields).map(key => ({
            id: key,
            title: key,
            type: 'text',
            placeholder: ''
          }));
          setCustomFields(customFieldsArray);
        }

        setShowPatientSelection(false);
      } catch (error) {
        console.error('Error loading intake form:', error);
        setSaveError('Failed to load intake form');
      } finally {
        setIsLoadingForm(false);
      }
    };

    loadIntakeForm();
  }, [formId]);


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCustomFieldChange = (fieldId, value) => {
    setFormData(prev => ({ ...prev, [fieldId]: value }));
  };

  const handleAddFieldClick = () => {
    setShowFieldModal(true);
  };

  const handleFieldModalChange = (e) => {
    const { name, value } = e.target;
    setNewField(prev => ({ ...prev, [name]: value }));
  };

  const handleAddField = () => {
    if (newField.title && newField.id) {
      setCustomFields(prev => [...prev, { ...newField }]);
      setFormData(prev => ({ ...prev, [newField.id]: '' }));
      setNewField({ type: 'text', title: '', id: '', placeholder: '' });
      setShowFieldModal(false);
    }
  };

  const handleRemoveField = (fieldId) => {
    setCustomFields(prev => prev.filter(field => field.id !== fieldId));
    setFormData(prev => {
      const newData = { ...prev };
      delete newData[fieldId];
      return newData;
    });
  };

  const handleFormSubmit = (e) => {
    // Prevent any form submission
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // This should ONLY navigate, NEVER save
    handleNextStep();
    return false;
  };

  const handleNextStep = async () => {
    // Validate domain selection
    if (!selectedDomain) {
      setSaveError('Please select a domain for the assessment');
      return;
    }

    // Load domain-specific prompt if available
    let domainPrompt = '';
    if (selectedDomain && selectedDomain.prompt) {
      domainPrompt = selectedDomain.prompt;
      setPrompt(domainPrompt);
    }

    setSaveError('');

    // Skip step 2 (prompt page) and directly submit the form
    // setCurrentStep(2); // Commented out - we're skipping this step

    // Directly call handleSubmit to save the form
    await handleSubmitDirect(domainPrompt);
  };

  const handleBackStep = () => {
    setCurrentStep(1);
  };

  const handleSaveToTemplates = () => {
    // TODO: Implement save to templates functionality
    alert('Prompt saved to templates!');
  };

  // Direct submit function (skips step 2)
  const handleSubmitDirect = async (domainPrompt = '') => {
    // Validate patient selection
    if (!selectedPatientId) {
      alert('Please select a patient first');
      return;
    }

    setIsSaving(true);
    setSaveError('');

    try {
      // Prepare intake form data
      const intakeFormData = {
        ...formData,
        patientId: selectedPatientId,
        selectedDomain: selectedDomain ? {
          id: selectedDomain._id || selectedDomain.id,
          name: selectedDomain.name
        } : null,
        customFields: customFields.reduce((acc, field) => {
          acc[field.id] = formData[field.id] || '';
          return acc;
        }, {}),
        uploadedFiles: uploadedFiles.map(file => ({
          name: file.name,
          s3Url: file.s3Url,
          size: file.size,
          type: file.type,
          uploadedAt: file.uploadedAt
        })),
        prompt: domainPrompt || prompt,
        status: 'draft'
      };

      let response;
      // If editing or viewing existing form, update it; otherwise create new one
      if (formId && (isEditMode || isViewMode)) {
        response = await intakeFormService.updateIntakeForm(formId, intakeFormData);
        alert('Intake form updated successfully! Report will be regenerated.');
      } else {
        response = await intakeFormService.createIntakeForm(intakeFormData);
        alert('Intake form saved successfully! Report generation started.');
      }

      // Navigate back to patient detail if we came from there, otherwise to intake forms list
      if (routePatientId) {
        navigate(`/patient/${routePatientId}`);
      } else {
        navigate('/intake-forms');
      }

    } catch (error) {
      console.error('Error saving intake form:', error);
      setSaveError(error.response?.data?.message || error.message || 'Failed to save intake form. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Safety check: only save on step 2
    if (currentStep !== 2) {
      alert('Please complete the prompt section first');
      return;
    }

    await handleSubmitDirect();
  };

  // Patient selection handlers
  const handlePatientSelect = (patientId) => {
    setSelectedPatientId(patientId);
    const selectedPatient = patientsData.find(p => p._id === patientId);
    if (selectedPatient) {
      setFormData(prev => ({
        ...prev,
        fullName: selectedPatient.fullName || '',
        email: selectedPatient.email || '',
        phoneNumber: selectedPatient.phoneNumber || '',
        age: selectedPatient.age || ''
      }));
    }
  };

  const handleContinueToForm = () => {
    if (!selectedPatientId) {
      alert('Please select a patient first');
      return;
    }
    setShowPatientSelection(false);
    setCurrentStep(1);
  };

  // File upload handlers
  const handleFileUpload = async (file) => {
    if (!file) return;

    // Validate patient selection first
    if (!selectedPatientId) {
      setUploadError('Please select a patient first before uploading a file');
      return;
    }

    // Validate file type
    const validTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
      'image/heic',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'text/plain'
    ];

    // Special handling for files without proper MIME type
    const fileExtension = file.name.split('.').pop().toLowerCase();
    const validExtensions = ['pdf', 'doc', 'docx', 'heic', 'jpeg', 'jpg', 'png', 'txt'];

    if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
      setUploadError('Please upload a valid file type (PDF, DOC, DOCX, HEIC, JPEG, PNG, TXT)');
      return;
    }

    // Validate file size (100MB)
    const maxSize = 100 * 1024 * 1024;
    if (file.size > maxSize) {
      setUploadError('File size must be less than 100MB');
      return;
    }

    setIsUploading(true);
    setUploadError('');

    try {
      // Use the intakeFormService method to properly handle API URL
      const result = await intakeFormService.uploadPdfAndCreateIntakeForm(file, selectedPatientId);
      console.log('Upload response:', result);

      // Check if extraction was successful
      if (result.success) {
        // Add file to uploaded files list with S3 URL
        setUploadedFiles(prev => [...prev, {
          name: file.name,
          size: file.size,
          type: file.type,
          uploadedAt: new Date().toISOString(),
          s3Url: result.data?.s3Url || '',
          data: result.data || null
        }]);
        setUploadError('');
      } else {
        throw new Error('Failed to upload file');
      }

    } catch (error) {
      console.error('Upload error:', error);
      setUploadError('Failed to upload and process file. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const parseAndMapData = (text) => {
    // Helper function to extract value after a label
    const extractValue = (label) => {
      const regex = new RegExp(`${label}:\\s*(.*)`, 'i');
      const match = text.match(regex);
      return match ? match[1].trim() : '';
    };

    // Extract and map data from the PDF text
    const parsedData = {
      // Identifying Information
      fullName: extractValue('Full Name'),
      dob: extractValue('Date of Birth'),
      age: extractValue('Age'),
      gender: extractValue('Gender'),
      raceEthnicity: extractValue('Race/Ethnicity'),
      primaryLanguage: extractValue('Primary Language'),
      address: extractValue('Address'),
      phoneNumber: extractValue('Phone'),
      email: extractValue('Email'),
      emergencyContact: extractValue('Emergency Contact'),

      // Referral & Presenting Concerns
      chiefComplaint: extractValue('Chief Complaint'),
      hasRiskFactors: extractValue('Risk Factors') ? 'yes' : 'no',
      riskFactorsDetails: extractValue('Risk Factors'),
      additionalComments: extractValue('Additional Comments'),

      // Medical & Psychiatric History
      currentMedications: extractValue('Current Medications')
    };

    // Update form data with parsed values
    setFormData(prev => ({
      ...prev,
      ...Object.fromEntries(
        Object.entries(parsedData).filter(([_, value]) => value !== '')
      )
    }));
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      // Upload multiple files sequentially
      for (const file of Array.from(files)) {
        await handleFileUpload(file);
      }
    }
  };

  const handleFileInputChange = async (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      // Upload multiple files sequentially
      for (const file of Array.from(files)) {
        await handleFileUpload(file);
      }
      // Reset the input so the same file can be uploaded again if needed
      e.target.value = '';
    }
  };

  const handleClickUpload = () => {
    fileInputRef.current?.click();
  };

  // Quill modules configuration
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      ['link', 'image'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['clean']
    ],
  };

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'link', 'image',
    'list', 'bullet'
  ];

  // Show loading state while loading existing form
  if (isLoadingForm) {
    return (
      <Layout>
        <div className="p-2.5 bg-white rounded-lg border border-[#C4CADA] min-h-screen">
          <div className="max-w-[1189px] mx-auto bg-white flex flex-col items-center justify-center gap-3" style={{ minHeight: '400px' }}>
            <div className="loading-spinner"></div>
            <p>Loading intake form...</p>
          </div>
        </div>
      </Layout>
    );
  }

  // Show patient selection screen first
  if (showPatientSelection && currentStep === 0) {
    return (
      <Layout>
        <div className="p-2.5 bg-white rounded-lg border border-[#C4CADA] min-h-screen">
          <div className="max-w-[1189px] mx-auto bg-white flex flex-col items-center gap-3">
            {/* Title */}
            <div className="w-full text-center text-[#001C3B] text-2xl font-bold font-['Poppins'] leading-10 mb-6">
              Select Patient for Intake Form
            </div>

            {isLoadingPatients ? (
              <div className="flex justify-center items-center py-20">
                <div className="loading-spinner"></div>
                <p className="ml-4">Loading patients...</p>
              </div>
            ) : (
              <>
                <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                  {patientsData.map((patient) => (
                    <div
                      key={patient._id}
                      onClick={() => handlePatientSelect(patient._id)}
                      className={`p-4 border rounded-lg cursor-pointer transition-all ${
                        selectedPatientId === patient._id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-300 hover:border-blue-300'
                      }`}
                    >
                      <div className="font-semibold text-lg">{patient.fullName || 'N/A'}</div>
                      <div className="text-sm text-gray-600">{patient.email || 'No email'}</div>
                      <div className="text-sm text-gray-600">{patient.phoneNumber || 'No phone'}</div>
                      <div className="text-sm text-gray-600">Age: {patient.age || 'N/A'}</div>
                    </div>
                  ))}
                </div>

                {patientsData.length === 0 && (
                  <div className="text-center py-10">
                    <p className="text-gray-600">No patients found. Please add a patient first.</p>
                  </div>
                )}

                <div className="w-full flex justify-center mt-6">
                  <button
                    onClick={handleContinueToForm}
                    disabled={!selectedPatientId}
                    className="px-8 py-3 bg-[#1E88E5] text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-600 transition-colors"
                  >
                    Continue to Form
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-2.5 bg-white rounded-lg border border-[#C4CADA] min-h-screen">
        <div className="max-w-[1189px] mx-auto bg-white flex flex-col items-center gap-3">

          {/* Title */}
          <div className="w-full text-center text-[#001C3B] text-2xl font-bold font-['Poppins'] leading-10">
            {currentStep === 1 ? (
              isViewMode ? 'View Intake Form' : isEditMode ? 'Edit Intake Form' : 'Initial Assessment Report'
            ) : (
              `${selectedDomain?.name || 'Clinical Psychology'} Prompt`
            )}
          </div>
          {isViewMode && currentStep === 1 && (
            <div className="w-full text-center text-gray-600 text-sm mb-2">
              This form is in view-only mode. Click Edit to make changes.
            </div>
          )}

          {currentStep === 1 ? (
            // STEP 1: Form Section
            <form onSubmit={handleFormSubmit} className="w-full flex flex-col items-center gap-3">

          {/* Select Assessment Domain Section */}
          <div className="w-full bg-white rounded-lg border border-[#C4CADA] flex flex-col justify-center items-center p-5">
            <div className="w-full text-[#111827] text-base font-semibold font-['Poppins'] leading-10 mb-3">
              Select Assessment Domain
            </div>
            {isLoadingDomains ? (
              <div className="w-full flex justify-center items-center py-4">
                <div className="text-[#6B7280] text-sm font-normal font-['Poppins']">
                  Loading domains...
                </div>
              </div>
            ) : (
              <div className="w-full flex justify-center items-center gap-6 flex-wrap">
                {domains.map((domain) => {
                  const isForensic = domain.name === 'Forensic Psychological';
                  const isDisabled = isForensic;
                  
                  return (
                    <div key={domain._id} className="flex flex-col items-center gap-1">
                      <button
                        type="button"
                        onClick={() => !isDisabled && setSelectedDomain(domain)}
                        disabled={isDisabled}
                        className={`px-7 py-1 rounded-md border border-[#001C3B] text-sm font-medium font-['Poppins'] leading-7 transition-colors ${
                          isDisabled
                            ? 'bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed'
                            : selectedDomain?._id === domain._id
                            ? 'bg-blue-100 text-[#001C3B]'
                            : 'bg-white text-[#001C3B] hover:bg-blue-50'
                        }`}
                      >
                        {domain.name}
                      </button>
                      {isForensic && (
                        <span className="text-xs text-gray-500 font-normal font-['Poppins']">
                          Coming soon
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* File Upload Section */}
          <div
            className={`w-full p-[34px] bg-white rounded-2xl border-2 flex flex-col justify-center items-center gap-4 cursor-pointer transition-colors ${
              isDragging
                ? 'border-blue-500 bg-blue-50'
                : 'border-[#424242] hover:border-blue-400'
            } ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={handleClickUpload}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx,.heic,.jpeg,.jpg,.png,.txt"
              onChange={handleFileInputChange}
              className="hidden"
              disabled={isUploading}
              multiple
            />

            {isUploading ? (
              <>
                <div className="text-center text-blue-500 text-[60px] font-black leading-[60px]">
                  ⏳
                </div>
                <div className="text-center text-blue-600 text-xl font-bold font-['Poppins'] leading-7">
                  Processing file...
                </div>
              </>
            ) : (
              <>
                <div className="text-center text-[#424242] text-[60px] font-black font-['Font Awesome 5 Free'] leading-[60px]">
                  📁
                </div>
                <div className="text-center text-[#6B7280] text-sm font-normal font-['Poppins'] leading-5 px-4 mb-2">
                  Please upload any relevant documents here. This can include background information, previous intake assessments, or any test data you have. If you don't have these, no worries — just continue to fill out the form below.
                </div>
                <div className="text-center text-[#001C3B] text-xl font-bold font-['Poppins'] leading-7">
                  Drag and drop files here
                </div>
                <div className="flex items-start gap-0">
                  <div className="text-center text-[#001C3B] text-xl font-semibold font-['Inter'] leading-7">Or </div>
                  <div className="text-center text-[#001C3B] text-xl font-semibold font-['Poppins'] leading-7">
                    click to select files
                  </div>
                </div>
                <div className="flex flex-col items-center">
                  <div className="text-center text-[#6B7280] text-sm font-normal font-['Poppins'] leading-5">
                    Supported: PDF, DOC, DOCX, HEIC, JPEG, PNG, TXT
                  </div>
                  <div className="text-center text-[#6B7280] text-sm font-normal font-['Poppins'] leading-5">
                    Max file size: 100MB
                  </div>
                </div>
              </>
            )}

            {uploadError && (
              <div className="text-center text-red-500 text-sm font-medium font-['Poppins'] leading-5">
                {uploadError}
              </div>
            )}
          </div>

          {/* File Upload Instructions */}
          <div className="w-full text-[#6B7280] text-sm font-medium font-['Poppins'] leading-5 -mt-2">
            <span className="font-semibold">Instructions:</span> Ensure all files are accurately labeled with their corresponding test names before uploading.
          </div>

          {/* Uploaded Files List */}
          {uploadedFiles.length > 0 && (
            <div className="w-full p-5 bg-white rounded-lg border border-[#C4CADA] flex flex-col gap-4">
              <div className="text-[#111827] text-base font-semibold font-['Poppins'] leading-6">
                Uploaded Files ({uploadedFiles.length})
              </div>
              <div className="flex flex-col gap-3">
                {uploadedFiles.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg"
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <div className="text-green-600 text-2xl">✓</div>
                      <div className="flex flex-col">
                        <div className="text-[#001C3B] text-sm font-medium font-['Poppins']">
                          {file.name}
                        </div>
                        <div className="text-[#6B7280] text-xs font-normal font-['Poppins']">
                          {(file.size / 1024).toFixed(2)} KB • Uploaded {new Date(file.uploadedAt).toLocaleTimeString()}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setUploadedFiles(prev => prev.filter((_, i) => i !== index));
                      }}
                      className="text-red-500 hover:text-red-700 text-sm font-medium px-3 py-1 rounded hover:bg-red-50 transition-colors"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Identifying Information - HIDDEN: User information not required to show */}
          <div className="w-full p-5 bg-white rounded-lg border border-[#C4CADA] flex flex-col gap-5" style={{ display: 'none' }}>
            <div className="text-[#111827] text-base font-semibold font-['Poppins'] capitalize leading-10">
              Identifying Information
            </div>

            <div className="flex flex-col gap-4">
              {/* Row 1 */}
              <div className="flex gap-4">
                <div className="flex-1 flex flex-col gap-1">
                  <label className="text-[#001C3B] text-sm font-medium font-['Poppins'] leading-[21px]">
                    Full Name <span className="text-[#FF0000]">*</span>
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className="h-9 px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex-1 flex flex-col gap-1">
                  <label className="text-[#001C3B] text-sm font-medium font-['Poppins'] leading-[21px]">
                    DOB <span className="text-[#FF0000]">*</span>
                  </label>
                  <input
                    type="text"
                    name="dob"
                    value={formData.dob}
                    onChange={handleInputChange}
                    className="h-9 px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-['Poppins']"
                  />
                </div>

                <div className="flex-1 flex flex-col gap-1">
                  <label className="text-[#001C3B] text-sm font-medium font-['Poppins'] leading-[21px]">
                    Age <span className="text-[#FF0000]">*</span>
                  </label>
                  <input
                    type="number"
                    name="age"
                    value={formData.age}
                    onChange={handleInputChange}
                    className="h-9 px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Row 2 */}
              <div className="flex gap-4">
                <div className="flex-1 flex flex-col gap-1">
                  <label className="text-[#001C3B] text-sm font-medium font-['Poppins'] leading-[21px]">
                    Phone Number <span className="text-[#FF0000]">*</span>
                  </label>
                  <input
                    type="text"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    placeholder="000-000-0000"
                    className="h-9 px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex-1 flex flex-col gap-1">
                  <label className="text-[#001C3B] text-sm font-medium font-['Poppins'] leading-[21px]">
                    Emergency Contact <span className="text-[#FF0000]">*</span>
                  </label>
                  <input
                    type="tel"
                    name="emergencyContact"
                    value={formData.emergencyContact}
                    onChange={handleInputChange}
                    placeholder="000-000-0000"
                    className="h-9 px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex-1 flex flex-col gap-1">
                  <label className="text-[#001C3B] text-sm font-medium font-['Poppins'] leading-6 tracking-wide">
                    Email Address <span className="text-[#FF0000]">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="h-9 px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Row 3 */}
              <div className="flex gap-4">
                <div className="flex-1 flex flex-col gap-1">
                  <label className="text-[#001C3B] text-sm font-medium font-['Poppins'] leading-[21px]">
                    Primary Language <span className="text-[#FF0000]">*</span>
                  </label>
                  <select
                    name="primaryLanguage"
                    value={formData.primaryLanguage}
                    onChange={handleInputChange}
                    className="h-9 px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-['Poppins']"
                  >
                    <option value="">Select</option>
                    <option value="English">English</option>
                    <option value="Spanish">Spanish</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="flex-1 flex flex-col gap-1">
                  <label className="text-[#001C3B] text-sm font-medium font-['Poppins'] leading-[21px]">
                    Race/Ethnicity <span className="text-[#FF0000]">*</span>
                  </label>
                  <select
                    name="raceEthnicity"
                    value={formData.raceEthnicity}
                    onChange={handleInputChange}
                    className="h-9 px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-['Poppins']"
                  >
                    <option value="">Select</option>
                    <option value="Asian">Asian</option>
                    <option value="Black">Black</option>
                    <option value="Hispanic">Hispanic</option>
                    <option value="White">White</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              {/* Row 4 */}
              <div className="flex gap-4">
                <div className="flex-1 flex flex-col gap-1">
                  <label className="text-[#001C3B] text-sm font-medium font-['Poppins'] leading-[21px]">
                    Gender <span className="text-[#FF0000]">*</span>
                  </label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    className="h-9 px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-['Poppins']"
                  >
                    <option value="">Select</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="flex-1 flex flex-col gap-1">
                  <label className="text-[#001C3B] text-sm font-medium font-['Poppins'] leading-[21px]">
                    Blood Group
                  </label>
                  <select
                    name="bloodGroup"
                    value={formData.bloodGroup}
                    onChange={handleInputChange}
                    className="h-9 px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-['Poppins']"
                  >
                    <option value="">Select</option>
                    <option value="A+">A+</option>
                    <option value="A-">A-</option>
                    <option value="B+">B+</option>
                    <option value="B-">B-</option>
                    <option value="AB+">AB+</option>
                    <option value="AB-">AB-</option>
                    <option value="O+">O+</option>
                    <option value="O-">O-</option>
                  </select>
                </div>
              </div>

              {/* Address */}
              <div className="flex flex-col gap-1">
                <label className="text-[#001C3B] text-sm font-medium font-['Poppins'] leading-6 tracking-wide">
                  Address <span className="text-[#FF0000]">*</span>
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  className="h-9 px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Referral & Presenting Concerns */}
          <div className="w-full p-5 bg-white rounded-lg border border-[#C4CADA] flex flex-col gap-5">
            <div className="text-[#111827] text-base font-semibold font-['Poppins'] capitalize leading-10">
              Referral & Presenting Concerns
            </div>

            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-[#001C3B] text-sm font-medium font-['Poppins'] leading-6 tracking-wide">
                  Referral Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="chiefComplaint"
                  value={formData.chiefComplaint}
                  onChange={handleInputChange}
                  rows="5"
                  required
                  className="px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="Enter the reason for referral..."
                />
              </div>

              {/* Risk Factors */}
              <div className="flex flex-col gap-3">
                <label className="text-[#001C3B] text-sm font-medium font-['Poppins'] leading-6 tracking-wide">
                  Risk Factors
                </label>
                <div className="text-[#6B7280] text-sm font-normal font-['Poppins'] leading-5">
                  Are there any immediate safety or risk concerns? (e.g., self-harm, suicidal thoughts, risk to others, severe symptoms)
                </div>
                <div className="flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="hasRiskFactors"
                      value="no"
                      checked={formData.hasRiskFactors === 'no'}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-blue-600 focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-[#001C3B] text-sm font-medium font-['Poppins']">No</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="hasRiskFactors"
                      value="yes"
                      checked={formData.hasRiskFactors === 'yes'}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-blue-600 focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-[#001C3B] text-sm font-medium font-['Poppins']">Yes</span>
                  </label>
                </div>

                {formData.hasRiskFactors === 'yes' && (
                  <div className="flex flex-col gap-1 mt-2">
                    <label className="text-[#001C3B] text-sm font-medium font-['Poppins'] leading-5">
                      Please describe the risk factors and safety concerns:
                    </label>
                    <textarea
                      name="riskFactorsDetails"
                      value={formData.riskFactorsDetails}
                      onChange={handleInputChange}
                      rows="4"
                      className="px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                      placeholder="Describe any immediate safety concerns, risk of self-harm, suicidal thoughts, risk to others, or severe symptoms..."
                    />
                  </div>
                )}
              </div>

              {/* Current Medications */}
              <div className="flex flex-col gap-1">
                <label className="text-[#001C3B] text-sm font-medium font-['Poppins'] leading-6 tracking-wide">
                  Current Medications (list names, dosage, duration) <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="currentMedications"
                  value={formData.currentMedications}
                  onChange={handleInputChange}
                  rows="5"
                  required
                  className="px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="Enter current medications (or write 'None' if not applicable)..."
                />
              </div>

              {/* Additional Comments */}
              <div className="flex flex-col gap-1">
                <label className="text-[#001C3B] text-sm font-medium font-['Poppins'] leading-6 tracking-wide">
                  Additional Comments / Additional Info (Optional)
                </label>
                <div className="text-[#6B7280] text-sm font-normal font-['Poppins'] leading-5 mb-2">
                  Add any additional notes or information you want to share — background context, relevant life events, extra observations, or anything not captured above.
                </div>
                <textarea
                  name="additionalComments"
                  value={formData.additionalComments}
                  onChange={handleInputChange}
                  rows="5"
                  className="px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="Enter any additional comments or information..."
                />
              </div>
            </div>
          </div>

          {/* Custom Fields */}
          <div className="w-full p-5 bg-white rounded-lg border border-[#C4CADA] flex flex-col gap-5">
            <div className="flex justify-between items-center">
              <div className="text-[#111827] text-base font-semibold font-['Poppins'] capitalize leading-10">
                Custom fields
              </div>
              <button
                type="button"
                onClick={handleAddFieldClick}
                className="px-4 py-2 bg-[#1E88E5] text-white rounded-md text-sm font-medium font-['Poppins'] hover:bg-blue-600 transition-colors"
              >
                + Add Field
              </button>
            </div>

            {/* Display Custom Fields */}
            {customFields.length > 0 && (
              <div className="flex flex-col gap-4">
                {customFields.map((field) => (
                  <div key={field.id} className="flex gap-2 items-start">
                    <div className="flex-1 flex flex-col gap-1">
                      <label className="text-[#001C3B] text-sm font-medium font-['Poppins'] leading-[21px]">
                        {field.title}
                      </label>
                      {field.type === 'text' ? (
                        <input
                          type="text"
                          id={field.id}
                          name={field.id}
                          value={formData[field.id] || ''}
                          onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
                          placeholder={field.placeholder}
                          className="h-9 px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      ) : (
                        <textarea
                          id={field.id}
                          name={field.id}
                          value={formData[field.id] || ''}
                          onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
                          placeholder={field.placeholder}
                          rows="3"
                          className="px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                        />
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveField(field.id)}
                      className="mt-7 text-red-500 hover:text-red-700 text-xl font-bold"
                      title="Remove field"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            {customFields.length === 0 && (
              <div className="text-center text-[#6B7280] text-sm font-normal font-['Poppins'] py-4">
                No custom fields added yet. Click "Add Field" to create one.
              </div>
            )}
          </div>

          {/* Quality Assurance Warning */}
          <div className="w-full p-5 bg-amber-50 rounded-lg border-2 border-amber-400 flex flex-col gap-3">
            <div className="flex items-start gap-3">
              <div className="text-amber-600 text-2xl font-bold mt-1">⚠️</div>
              <div className="flex-1">
                <div className="text-amber-900 text-base font-bold font-['Poppins'] leading-6 mb-3">
                  Quality Assurance Warning / Instruction for Clinician Review
                </div>
                <ul className="list-none flex flex-col gap-2 text-amber-900 text-sm font-normal font-['Poppins'] leading-6">
                  <li className="flex items-start gap-2">
                    <span className="mt-1">○</span>
                    <span>All uploaded documents, prior test data, and intake information must be reviewed for completeness and accuracy.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-1">○</span>
                    <span>Important contextual factors — including forensic context, history of suicidal behavior, risk factors, clinical observations — should be carefully evaluated.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-1">○</span>
                    <span>The clinician holds ultimate responsibility; the system (or AI) serves only as documentation assistance, not as a substitute for professional clinical judgment.</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Add Field Modal */}
          {showFieldModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-[#001C3B] text-lg font-semibold font-['Poppins']">
                    Add Custom Field
                  </h3>
                  <button
                    type="button"
                    onClick={() => setShowFieldModal(false)}
                    className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                  >
                    ×
                  </button>
                </div>

                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-1">
                    <label className="text-[#001C3B] text-sm font-medium font-['Poppins']">
                      Field Type <span className="text-[#FF0000]">*</span>
                    </label>
                    <select
                      name="type"
                      value={newField.type}
                      onChange={handleFieldModalChange}
                      className="h-9 px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="text">Text Input</option>
                      <option value="textarea">Text Area</option>
                    </select>
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className="text-[#001C3B] text-sm font-medium font-['Poppins']">
                      Field Title <span className="text-[#FF0000]">*</span>
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={newField.title}
                      onChange={handleFieldModalChange}
                      placeholder="Enter field title"
                      className="h-9 px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className="text-[#001C3B] text-sm font-medium font-['Poppins']">
                      Field ID <span className="text-[#FF0000]">*</span>
                    </label>
                    <input
                      type="text"
                      name="id"
                      value={newField.id}
                      onChange={handleFieldModalChange}
                      placeholder="e.g., customField1"
                      className="h-9 px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-xs text-gray-500">Unique identifier for this field</span>
                  </div>

                  <div className="flex flex-col gap-1">
                    <label className="text-[#001C3B] text-sm font-medium font-['Poppins']">
                      Placeholder
                    </label>
                    <input
                      type="text"
                      name="placeholder"
                      value={newField.placeholder}
                      onChange={handleFieldModalChange}
                      placeholder="Enter placeholder text"
                      className="h-9 px-3 py-1.5 bg-white rounded-md border border-[#C4CADA] focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="flex gap-3 mt-4">
                    <button
                      type="button"
                      onClick={() => setShowFieldModal(false)}
                      className="flex-1 h-10 bg-white rounded-lg border border-[#001C3B] text-[#001C3B] text-sm font-medium font-['Poppins'] hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleAddField}
                      className="flex-1 h-10 bg-[#1E88E5] rounded-lg text-white text-sm font-medium font-['Poppins'] hover:bg-blue-600 transition-colors"
                    >
                      Add Field
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {saveError && (
            <div className="w-full p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm font-medium font-['Poppins']">
                {saveError}
              </p>
            </div>
          )}

          {/* Action Buttons - Step 1 */}
          <div className="w-full flex justify-end items-center gap-3 pb-6">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="w-[93px] h-12 bg-white rounded-lg border border-[#001C3B] text-[#001C3B] text-base font-medium font-['Poppins'] leading-5 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!formData.chiefComplaint.trim() || !formData.currentMedications.trim()}
              className="w-[180px] h-12 bg-[#1E88E5] rounded-lg border border-[#E6E6E6] text-white text-base font-medium font-['Poppins'] leading-5 hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Submit Intake-Form
            </button>
          </div>
          </form>
          ) : currentStep === 2 ? (
            // STEP 2: Prompt Section
            <>
              {/* Prompt Editor Section */}
              <div className="w-full bg-white rounded-lg border border-[#C4CADA] flex flex-col p-5">
                <div className="w-full mb-4">
                  <label className="text-[#111827] text-base font-semibold font-['Poppins'] leading-10 mb-2 block">
                    Enter Prompt Here
                  </label>
                  <div className="prompt-editor-container">
                    <ReactQuill
                      theme="snow"
                      value={prompt}
                      onChange={setPrompt}
                      modules={modules}
                      formats={formats}
                      placeholder="Enter your prompt here..."
                      className="bg-white"
                      style={{
                        height: '400px',
                        marginBottom: '50px'
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Error Message */}
              {saveError && (
                <div className="w-full p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-600 text-sm font-medium font-['Poppins']">
                    {saveError}
                  </p>
                </div>
              )}

              {/* Action Buttons - Step 2 */}
              <div className="w-full flex justify-between items-center pb-6">
                <button
                  type="button"
                  onClick={handleBackStep}
                  className="w-[93px] h-12 bg-white rounded-lg border border-[#001C3B] text-[#001C3B] text-base font-medium font-['Poppins'] leading-5 hover:bg-gray-50 transition-colors"
                >
                  Back
                </button>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleSaveToTemplates}
                    className="h-12 px-6 bg-white rounded-lg border border-[#001C3B] text-[#001C3B] text-base font-medium font-['Poppins'] leading-5 hover:bg-gray-50 transition-colors"
                  >
                    Save to templates
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isSaving}
                    className="w-[135px] h-12 bg-[#1E88E5] rounded-lg border border-[#E6E6E6] text-white text-base font-medium font-['Poppins'] leading-5 hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isSaving ? (
                      <>
                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Saving...
                      </>
                    ) : (
                      'Next'
                    )}
                  </button>
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </Layout>
  );
};

export default IntakeForm;
